import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { verificationService } from '../../../services/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';

const UserVerificationTab = () => {
  const { user } = useAuth();
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    loadPendingVerifications();
  }, []);

  const loadPendingVerifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await verificationService.getPendingVerifications();
      // Transform data to match component structure
      const requests = [];
      
      data?.forEach((profile) => {
        // Photo verification pending
        if (profile?.photoVerificationStatus === 'pending') {
          requests.push({
            id: `PHOTO_${profile?.id}`,
            userId: profile?.id,
            userName: profile?.displayName || profile?.email || 'Unknown User',
            userEmail: profile?.email,
            submittedDate: profile?.photoVerifiedAt || profile?.createdAt,
            status: 'pending',
            verificationType: 'photo',
            documentType: 'Photo Selfie',
            documentImage: profile?.verificationImages?.photoSelfie || null,
            selfieImage: profile?.verificationImages?.photoSelfie || null
          });
        }
        
        // Identity verification pending
        if (profile?.identityVerificationStatus === 'pending') {
          requests.push({
            id: `IDENTITY_${profile?.id}`,
            userId: profile?.id,
            userName: profile?.displayName || profile?.email || 'Unknown User',
            userEmail: profile?.email,
            submittedDate: profile?.identityVerifiedAt || profile?.createdAt,
            status: 'pending',
            verificationType: 'identity',
            documentType: 'Government ID',
            documentImage: profile?.verificationImages?.idDocument || null,
            selfieImage: profile?.verificationImages?.idSelfie || null
          });
        }
      });
      
      // Sort by submission date (most recent first)
      requests.sort((a, b) => {
        const dateA = new Date(a.submittedDate || 0);
        const dateB = new Date(b.submittedDate || 0);
        return dateB - dateA;
      });
      
      setVerificationRequests(requests);
    } catch (err) {
      console.error('Error loading verification requests:', err);
      setError(err?.message || 'Failed to load verification requests.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (requestId, action) => {
    if (!user?.id) {
      setError('Please log in as admin.');
      return;
    }

    const request = verificationRequests?.find((r) => r?.id === requestId);
    if (!request) return;

    // If rejecting, show rejection reason modal
    if (action === 'reject') {
      setRejectingRequestId(requestId);
      setRejectionReason('');
      return;
    }

    // Approve action
    setProcessingId(requestId);
    setError(null);
    try {
      if (request?.verificationType === 'photo') {
        await verificationService.approvePhotoVerification(request.userId, user.id);
      } else if (request?.verificationType === 'identity') {
        await verificationService.approveIdentityVerification(request.userId, user.id);
      }

      await loadPendingVerifications();
    } catch (err) {
      console.error('Error processing verification:', err);
      const msg =
        err?.message ||
        err?.error_description ||
        err?.details ||
        'Failed to process verification. If this persists, run the latest Supabase migration (admin RLS).';
      setError(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectingRequestId || !user?.id) return;

    const request = verificationRequests?.find((r) => r?.id === rejectingRequestId);
    if (!request) return;

    setProcessingId(rejectingRequestId);
    setError(null);
    try {
      if (request?.verificationType === 'photo') {
        await verificationService.rejectPhotoVerification(request.userId, user.id, rejectionReason || null);
      } else if (request?.verificationType === 'identity') {
        await verificationService.rejectIdentityVerification(request.userId, user.id, rejectionReason || null);
      }

      await loadPendingVerifications();
      setRejectingRequestId(null);
      setRejectionReason('');
    } catch (err) {
      console.error('Error rejecting verification:', err);
      const msg =
        err?.message ||
        err?.error_description ||
        err?.details ||
        'Failed to reject verification. If this persists, run the latest Supabase migration (admin RLS).';
      setError(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'approved':
        return 'text-success bg-success/10';
      case 'rejected':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const pendingRequests = verificationRequests?.filter((req) => req?.status === 'pending');

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading verification requests...</p>
        </div>
      </div>
    );
  }

  if (error && verificationRequests?.length === 0) {
    return (
      <div className="p-12 text-center bg-card rounded-xl">
        <Icon name="AlertCircle" size={48} color="var(--color-error)" className="mx-auto mb-3" />
        <p className="text-base text-error mb-1">Error loading verifications</p>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={loadPendingVerifications} variant="outline">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-heading font-semibold">Verification Requests</h3>
          <p className="text-sm text-muted-foreground">
            {pendingRequests?.length} pending request{pendingRequests?.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" iconName="RefreshCw" onClick={loadPendingVerifications}>
            Refresh
          </Button>
        </div>
      </div>

      <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-semibold text-foreground mb-1">Admin Verification Guidelines:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Check that photos match profile photos</li>
              <li>Verify pose requirements are met (front-facing, clear face, good lighting)</li>
              <li>For ID verification: Ensure ID is valid and matches selfie</li>
              <li>Click images to view full size</li>
              <li>Provide rejection reason if rejecting (helps users improve)</li>
            </ul>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-sm text-error">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {verificationRequests?.map((request) => (
          <div
            key={request?.id}
            className="bg-card rounded-xl shadow-warm overflow-hidden border border-border"
          >
            <div className="p-4 bg-muted/50 border-b border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-mono font-medium">{request?.id}</span>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                    request?.status
                  )}`}
                >
                  {request?.status?.charAt(0)?.toUpperCase() + request?.status?.slice(1)}
                </span>
              </div>
              <p className="text-sm font-medium">{request?.userName}</p>
              <p className="text-xs text-muted-foreground font-mono">{request?.userEmail}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Type: {request?.verificationType === 'photo' ? 'Photo Authenticity' : 'Identity Verification'}
              </p>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="FileText" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-caption font-semibold">Document Type</span>
                </div>
                <p className="text-sm">{request?.documentType}</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Calendar" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-caption font-semibold">Submitted</span>
                </div>
                <p className="text-sm">{formatDate(request?.submittedDate)}</p>
              </div>

              {request?.documentImage || request?.selfieImage ? (
                <div className="grid grid-cols-2 gap-3">
                  {request?.documentImage && (
                    <div>
                      <p className="text-xs font-caption font-semibold mb-2">
                        {request?.verificationType === 'photo' ? 'Selfie Photo' : 'ID Document'}
                      </p>
                      <div 
                        className="aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage(request?.documentImage)}
                      >
                        <Image
                          src={request?.documentImage}
                          alt="Verification document"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                  {request?.selfieImage && (
                    <div>
                      <p className="text-xs font-caption font-semibold mb-2">
                        {request?.verificationType === 'photo' ? 'Selfie Photo' : 'ID Selfie'}
                      </p>
                      <div 
                        className="aspect-video bg-muted rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedImage(request?.selfieImage)}
                      >
                        <Image
                          src={request?.selfieImage}
                          alt="Verification selfie"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <Icon name="Image" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground mb-2">
                    No images uploaded yet
                  </p>
                  <p className="text-xs text-warning">
                    ⚠️ User may not have uploaded files, or storage bucket not configured
                  </p>
                </div>
              )}

              {request?.status === 'pending' && (
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="success"
                    fullWidth
                    iconName="CheckCircle"
                    iconPosition="left"
                    disabled={!!processingId}
                    onClick={() => handleVerification(request?.id, 'approve')}
                  >
                    {processingId === request?.id ? 'Saving…' : 'Approve'}
                  </Button>
                  <Button
                    variant="danger"
                    fullWidth
                    iconName="XCircle"
                    iconPosition="left"
                    disabled={!!processingId}
                    onClick={() => handleVerification(request?.id, 'reject')}
                  >
                    Reject
                  </Button>
                </div>
              )}

              {request?.status !== 'pending' && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${getStatusColor(request?.status)}`}>
                  <Icon
                    name={request?.status === 'approved' ? 'CheckCircle' : 'XCircle'}
                    size={16}
                  />
                  <span className="text-sm font-medium">
                    {request?.status === 'approved' ? 'Verification Approved' : 'Verification Rejected'}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {verificationRequests?.length === 0 && !loading && (
        <div className="p-12 text-center bg-card rounded-xl">
          <Icon name="BadgeCheck" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-base text-muted-foreground mb-1">No verification requests</p>
          <p className="text-sm text-muted-foreground">New requests will appear here when users submit verifications</p>
        </div>
      )}

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/90 backdrop-blur-sm"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-primary transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
            <img
              src={selectedImage}
              alt="Verification image"
              className="w-full h-auto max-h-[90vh] object-contain rounded-lg shadow-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {rejectingRequestId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-xl shadow-warm-lg border border-border max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">Reject Verification</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Please provide a reason for rejection (optional but recommended):
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Photo quality too low, ID not clear, pose requirements not met..."
              className="w-full min-h-[100px] p-3 border border-border rounded-lg bg-background text-foreground mb-4 resize-none"
            />
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                disabled={!!processingId}
                onClick={() => {
                  setRejectingRequestId(null);
                  setRejectionReason('');
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="danger" 
                disabled={!!processingId}
                onClick={handleRejectConfirm}
              >
                {processingId ? 'Saving…' : 'Reject Verification'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserVerificationTab;
